import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
export const fadeUp = {
  hidden: {
    opacity: 0,
    y: 22
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function Pill({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-white/85 backdrop-blur" data-id="element-135">
      {children}
    </span>;
}
export function SectionLabel({
  children,
  dark = false
}: {
  children: React.ReactNode;
  dark?: boolean;
}) {
  return <div className={`mb-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] ${dark ? 'border-white/15 bg-white/10 text-blue-100' : 'border-slate-200 bg-white text-slate-600 shadow-sm'}`} data-id="element-136">
      <span className="h-2 w-2 rounded-full bg-sky-400" data-id="element-137" />
      {children}
    </div>;
}
type CTAProps = {
  children: React.ReactNode;
  variant?: 'primary' | 'dark' | 'secondary' | 'alert';
  className?: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
};
export function CTAButton({
  children,
  variant = 'primary',
  className = '',
  to,
  href,
  onClick,
  type = 'button'
}: CTAProps) {
  const base = 'group inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-sm font-bold transition-all duration-300';
  const styles = variant === 'primary' ? 'bg-sky-400 text-slate-950 shadow-xl shadow-sky-500/20 hover:bg-sky-300' : variant === 'dark' ? 'bg-slate-950 text-white hover:bg-slate-800' : variant === 'alert' ? 'bg-red-600 text-white shadow-lg shadow-red-600/20 hover:bg-red-700' : 'border border-white/20 bg-white/10 text-white backdrop-blur hover:bg-white/15';
  const content = <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-138" />
    </>;
  const cls = `${base} ${styles} ${className}`;
  if (to) {
    return <Link to={to} className={cls} data-id="element-139">
        {content}
      </Link>;
  }
  if (href) {
    return <a href={href} className={cls} data-id="element-140">
        {content}
      </a>;
  }
  return <button type={type} onClick={onClick} className={cls} data-id="element-141">
      {content}
    </button>;
}
export function PageHero({
  eyebrow,
  title,
  subtitle,
  children
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return <section className="relative overflow-hidden bg-slate-950 pt-24 pb-12 text-white sm:pt-32 sm:pb-20" data-id="element-142">
      <div className="absolute left-1/2 top-0 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-sky-500/20 blur-3xl" data-id="element-143" />
      <div className="absolute inset-0 opacity-[0.07]" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)',
      backgroundSize: '42px 42px'
    }} data-id="element-144" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-145">
        <SectionLabel dark data-id="element-146">{eyebrow}</SectionLabel>
        <h1 className="max-w-4xl text-3xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-id="element-147">
          {title}
        </h1>
        {subtitle && <p className="mt-5 max-w-2xl text-base leading-7 text-slate-300 sm:mt-6 sm:text-lg sm:leading-8" data-id="element-148">
            {subtitle}
          </p>}
        {children && <div className="mt-6 sm:mt-8" data-id="element-149">{children}</div>}
      </div>
    </section>;
}